import {
  ANGULOS_DE_FOTO,
  type AnguloDeFoto,
  registrarVistoriaSchema,
  ROTULO_DA_GRAVIDADE,
  ROTULO_DO_ANGULO,
  ROTULO_DO_TIPO_DE_AVARIA,
  ROTULO_DO_TIPO_DE_VISTORIA,
  rotuloDoNivelDeCombustivel,
  TIPOS_DE_VISTORIA,
  type TipoDeVistoria,
} from '@locaveri/shared'
import { Link, useParams } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ArrowLeft,
  ArrowRightLeft,
  Ban,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  Eye,
  ImageOff,
  LoaderCircle,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { NotFoundState } from '@/components/state/not-found-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useUrlDeLeitura } from '@/features/arquivos/url-de-leitura'
import {
  type AvariaLida,
  useAnularVistoria,
  useChecklist,
  useComparativo,
  useRegistrarVistoria,
  useVistoriasDaReserva,
  type Vistoria,
} from '@/features/vistorias/api'
import { BADGE_DA_GRAVIDADE, DiagramaDoCarro } from '@/features/vistorias/diagrama-do-carro'
import {
  corpoDaVistoria,
  distribuirErrosDaVistoria,
  type ErrosDaVistoria,
  FormularioDeVistoria,
  type FormularioDeVistoriaHandle,
  LegendaDeGravidade,
  VALOR_INICIAL_DA_VISTORIA,
  type ValorDaVistoria,
} from '@/features/vistorias/formulario-de-vistoria'
import { errorMessage } from '@/lib/api'
import { formatarDataHora, SEM_VALOR } from '@/lib/datas'
import { usePermissao } from '@/lib/permissoes'
import { cn } from '@/lib/utils'

/**
 * Vistorias de uma reserva — registrar, listar (com as anuladas), detalhar e
 * comparar.
 *
 * ---------------------------------------------------------------------------
 * A VISTORIA É A PROVA, E A TELA MOSTRA A PROVA DE VERDADE
 * ---------------------------------------------------------------------------
 * Contagem de fotos não convence ninguém numa disputa — a FOTO convence. O
 * bucket é privado, então toda exibição troca a CHAVE gravada por uma URL
 * assinada de leitura (`useUrlDeLeitura`); nunca se monta URL de bucket na
 * mão. Quem não tem `inspection.read` da pasta recebe 403 da assinatura, e a
 * miniatura vira um "sem leitura" discreto — a tela não quebra por causa de
 * uma imagem.
 *
 * O comparativo mostra o PAR de fotos de cada ângulo (saída à esquerda,
 * retorno à direita): é lado a lado que a avaria nova fica evidente, e as
 * avarias de cada lado ficam listadas sob as fotos, com a foto de detalhe
 * clicável quando existir.
 *
 * ---------------------------------------------------------------------------
 * QUEM VALIDA É O SCHEMA COMPARTILHADO; QUEM DECIDE É A API
 * ---------------------------------------------------------------------------
 * O submit roda o `registrarVistoriaSchema` de `@locaveri/shared` — o MESMO
 * que a API executa — e espalha as mensagens do Zod seção a seção (o
 * formulário em si vive em `features/vistorias/formulario-de-vistoria`, o
 * mesmo que a troca de veículo usa duas vezes). O que o schema não sabe (409
 * de vistoria duplicada, 422 de status da reserva ou de item obrigatório do
 * checklist sem resposta) chega da API e vira toast com a mensagem DELA.
 * Mesma lógica no anular: o botão sempre abre o diálogo, e é o 409 do
 * servidor que diz "reserva finalizada não se mexe".
 */

const NUMERO_PT_BR = new Intl.NumberFormat('pt-BR')

function formatarKm(valor: number): string {
  return `${NUMERO_PT_BR.format(valor)} km`
}

/**
 * A API devolve só o UUID de quem registrou (`realizadaPorId`) — resolver o
 * nome exigiria a listagem de usuários, que nem todo papel com
 * `inspection.read` pode ler. O prefixo do id identifica o autor para quem
 * precisa (o id inteiro fica no `title`), sem prometer um nome que a resposta
 * não trouxe.
 */
function resumoDoAutor(realizadaPorId: string | null): string {
  return realizadaPorId === null ? SEM_VALOR : realizadaPorId.slice(0, 8)
}

/** O efeito colateral de cada tipo, escrito ANTES do clique — não depois. */
const EFEITO_DO_TIPO: Readonly<Record<TipoDeVistoria, string>> = {
  SAIDA: 'Registrar a saída move a reserva para EM ANDAMENTO e o veículo para EM LOCAÇÃO.',
  RETORNO:
    'Registrar o retorno deixa a devolução pendente de fechamento e manda o veículo para LIMPEZA.',
}

// ---------------------------------------------------------------------------
// Exibição de arquivos do storage (bucket privado → URL assinada)
// ---------------------------------------------------------------------------

interface FotoAssinadaProps {
  /** A CHAVE gravada (`tenants/<tenant>/<pasta>/<uuid>.<ext>`). */
  readonly chave: string
  readonly alt: string
  /** Dimensões da miniatura (altura/largura); o resto é do componente. */
  readonly className?: string
  readonly title?: string
  /** `true` para `object-contain` (assinatura) em vez de `object-cover`. */
  readonly conter?: boolean
}

/**
 * Uma imagem do storage: troca a chave por URL assinada, mostra skeleton
 * enquanto assina e um "sem leitura" discreto quando a assinatura falha (403
 * de pasta sem permissão, chave órfã, storage fora do ar). O clique abre a
 * imagem em nova aba pela MESMA URL assinada.
 */
function FotoAssinada({
  chave,
  alt,
  className,
  title,
  conter = false,
}: FotoAssinadaProps): ReactElement {
  const consulta = useUrlDeLeitura(chave)

  if (consulta.isPending) {
    return <Skeleton className={cn('w-full rounded-md', className)} />
  }

  if (consulta.data === undefined) {
    return (
      <div
        role="img"
        aria-label={`${alt} — sem leitura`}
        title={errorMessage(consulta.error)}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-0.5 rounded-md border',
          'border-dashed border-border bg-surface text-fg-muted',
          className,
        )}
      >
        <ImageOff className="size-4" aria-hidden="true" />
        <span className="text-[11px] leading-none">sem leitura</span>
      </div>
    )
  }

  return (
    <a
      href={consulta.data.urlDeLeitura}
      target="_blank"
      rel="noopener noreferrer"
      title={title ?? `${alt} — abrir em nova aba`}
      className={cn(
        'block w-full overflow-hidden rounded-md border border-border',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        conter && 'bg-elevated',
        className,
      )}
    >
      <img
        src={consulta.data.urlDeLeitura}
        alt={alt}
        loading="lazy"
        className={cn('h-full w-full', conter ? 'object-contain' : 'object-cover')}
      />
    </a>
  )
}

/** Miniatura + rótulo do ângulo — a unidade das galerias de foto. */
function FotoComRotulo({
  chave,
  angulo,
}: {
  readonly chave: string
  readonly angulo: AnguloDeFoto
}): ReactElement {
  return (
    <figure className="min-w-0 space-y-1">
      <FotoAssinada chave={chave} alt={`Foto: ${ROTULO_DO_ANGULO[angulo]}`} className="h-28" />
      <figcaption className="truncate text-xs text-fg-secondary" title={ROTULO_DO_ANGULO[angulo]}>
        {ROTULO_DO_ANGULO[angulo]}
      </figcaption>
    </figure>
  )
}

/** A assinatura como imagem pequena com legenda — documento, não decoração. */
function AssinaturaDoCliente({ chave }: { readonly chave: string }): ReactElement {
  return (
    <figure className="w-full max-w-xs space-y-1">
      <FotoAssinada chave={chave} alt="Assinatura do cliente" className="h-20" conter />
      <figcaption className="text-xs text-fg-muted">Assinatura do cliente</figcaption>
    </figure>
  )
}

const MINIATURAS_NA_LINHA = 4

/** As fotos reais na linha da tabela — contagem não é prova, foto é. */
function MiniaturasDaLinha({ vistoria }: { readonly vistoria: Vistoria }): ReactElement {
  const visiveis = vistoria.fotos.slice(0, MINIATURAS_NA_LINHA)
  const restante = vistoria.fotos.length - visiveis.length

  return (
    <div className="min-w-0 space-y-1">
      {visiveis.length === 0 ? (
        <span className="text-xs text-fg-muted">Sem fotos</span>
      ) : (
        <div className="flex items-center gap-1">
          {visiveis.map((foto) => (
            <FotoAssinada
              key={foto.id}
              chave={foto.arquivoChave}
              alt={`Foto: ${ROTULO_DO_ANGULO[foto.angulo]}`}
              title={ROTULO_DO_ANGULO[foto.angulo]}
              className="h-9 w-14 shrink-0"
            />
          ))}
          {restante > 0 ? (
            <span className="text-xs tabular-money text-fg-secondary">+{String(restante)}</span>
          ) : null}
        </div>
      )}
      <p className="whitespace-nowrap text-xs tabular-money text-fg-secondary">
        {vistoria.avarias.length} {vistoria.avarias.length === 1 ? 'avaria' : 'avarias'}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Avarias lidas (com a foto de detalhe clicável)
// ---------------------------------------------------------------------------

interface ItemDeAvariaProps {
  readonly avaria: AvariaLida
  readonly indice?: number | undefined
  /** Avaria NOVA do comparativo — o contorno de perigo a destaca. */
  readonly nova?: boolean
}

function ItemDeAvaria({ avaria, indice, nova = false }: ItemDeAvariaProps): ReactElement {
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border p-3',
        nova ? 'border-danger-fg/40 bg-danger/5' : 'border-border bg-surface',
      )}
    >
      {indice === undefined ? null : (
        <span className="tabular-money text-sm font-semibold text-foreground">
          {String(indice + 1)}.
        </span>
      )}
      <Badge variant={BADGE_DA_GRAVIDADE[avaria.gravidade]}>
        {ROTULO_DA_GRAVIDADE[avaria.gravidade]}
      </Badge>
      <span className="text-sm font-medium text-foreground">
        {ROTULO_DO_TIPO_DE_AVARIA[avaria.tipo]}
      </span>
      <span className="min-w-0 flex-1 text-sm text-fg-secondary">{avaria.descricao}</span>
      {avaria.fotoArquivoChave === null ? null : (
        <FotoAssinada
          chave={avaria.fotoArquivoChave}
          alt={`Foto da avaria: ${avaria.descricao}`}
          title="Abrir a foto da avaria em nova aba"
          className="h-12 w-16 shrink-0"
        />
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Registro
// ---------------------------------------------------------------------------

interface FormularioDeRegistroProps {
  readonly reservaId: string
}

function FormularioDeRegistro({ reservaId }: FormularioDeRegistroProps): ReactElement {
  const registrar = useRegistrarVistoria(reservaId)
  const formularioRef = useRef<FormularioDeVistoriaHandle | null>(null)

  const [tipo, setTipo] = useState<TipoDeVistoria>('SAIDA')
  const [valor, setValor] = useState<ValorDaVistoria>(VALOR_INICIAL_DA_VISTORIA)
  const [erros, setErros] = useState<ErrosDaVistoria>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [enviandoAssinatura, setEnviandoAssinatura] = useState(false)

  const ocupado = registrar.isPending || enviandoAssinatura
  const enviandoFoto = valor.enviandoAngulos.length > 0

  function limparFormulario(): void {
    setValor(VALOR_INICIAL_DA_VISTORIA)
    setErros({})
    setErroGeral(null)
    formularioRef.current?.limpar()
  }

  async function enviar(): Promise<void> {
    setErroGeral(null)
    setErros({})

    // A assinatura sobe antes da validação: o schema exige a chave, e um
    // "placeholder para validar primeiro" seria mentir para o próprio schema.
    // A chave fica cacheada no formulário, então repetir o submit não repete o
    // upload.
    let assinaturaArquivoChave: string | null = null
    setEnviandoAssinatura(true)

    try {
      assinaturaArquivoChave = (await formularioRef.current?.obterAssinaturaChave()) ?? null
    } catch (causa) {
      toast.error('Não foi possível enviar a assinatura', { description: errorMessage(causa) })
      return
    } finally {
      setEnviandoAssinatura(false)
    }

    if (assinaturaArquivoChave === null) {
      setErros({
        assinaturaArquivoChave:
          'a assinatura do cliente é obrigatória — vistoria sem assinatura não prova concordância',
      })
      return
    }

    const validado = registrarVistoriaSchema.safeParse({
      tipo,
      ...corpoDaVistoria(valor, assinaturaArquivoChave),
    })

    if (!validado.success) {
      const { porCampo, sobras } = distribuirErrosDaVistoria(validado.error.issues)
      setErros(porCampo)
      setErroGeral(sobras.length > 0 ? sobras.join(' · ') : null)
      return
    }

    // `validado.data` aqui é JSON-safe (só números e strings), ao contrário
    // dos formulários de dinheiro — ver o cabeçalho de `features/vistorias/api`.
    registrar.mutate(validado.data, {
      onSuccess: (registrada) => {
        toast.success(`${ROTULO_DO_TIPO_DE_VISTORIA[registrada.tipo]} registrada`, {
          description: EFEITO_DO_TIPO[registrada.tipo],
        })
        limparFormulario()
      },
      onError: (causa: unknown) => {
        // 409 (já existe vistoria válida do tipo) e 422 (status da reserva,
        // checklist obrigatório) — a mensagem é a da API, sem tradução local.
        toast.error('Não foi possível registrar a vistoria', {
          description: errorMessage(causa),
        })
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registrar vistoria</CardTitle>
        <CardDescription>
          Fotos por ângulo, checklist, avarias posicionadas no diagrama e a assinatura do cliente.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form
          noValidate
          className="space-y-6"
          onSubmit={(evento) => {
            evento.preventDefault()
            void enviar()
          }}
        >
          <div className="max-w-sm space-y-2">
            <p className="text-sm font-medium text-foreground">
              Tipo de vistoria
              <span aria-hidden="true" className="ml-1 text-fg-muted">
                *
              </span>
            </p>
            <div
              role="group"
              aria-label="Tipo de vistoria"
              className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface p-1"
            >
              {TIPOS_DE_VISTORIA.map((opcao) => (
                <button
                  key={opcao}
                  type="button"
                  aria-pressed={tipo === opcao}
                  onClick={() => {
                    setTipo(opcao)
                  }}
                  className={cn(
                    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    tipo === opcao
                      ? 'bg-elevated text-foreground'
                      : 'text-fg-secondary hover:text-foreground',
                  )}
                >
                  {ROTULO_DO_TIPO_DE_VISTORIA[opcao]}
                </button>
              ))}
            </div>
          </div>

          <FormularioDeVistoria
            ref={formularioRef}
            titulo={ROTULO_DO_TIPO_DE_VISTORIA[tipo]}
            descricao={EFEITO_DO_TIPO[tipo]}
            valor={valor}
            aoMudar={setValor}
            erros={erros}
            desabilitado={ocupado}
          />

          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={ocupado || enviandoFoto}>
              {ocupado ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <ClipboardCheck aria-hidden="true" />
              )}
              Registrar vistoria
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Detalhe de uma vistoria
// ---------------------------------------------------------------------------

interface DialogoDeDetalheProps {
  /** `null` = fechado. */
  readonly vistoria: Vistoria | null
  readonly aoFechar: () => void
}

/**
 * Tudo que a vistoria gravou, legível: as respostas do checklist com o RÓTULO
 * do item vigente (a chave crua só quando o item saiu da configuração — a
 * vistoria é imutável, o checklist não), as fotos de verdade, as avarias no
 * diagrama e a assinatura.
 */
function DialogoDeDetalhe({ vistoria, aoFechar }: DialogoDeDetalheProps): ReactElement {
  // O checklist VIGENTE dá o rótulo humano de cada chave respondida. Item que
  // saiu da configuração aparece pela chave — melhor a chave do que esconder a
  // resposta gravada.
  const checklist = useChecklist()
  const rotuloDoItem = useMemo(
    () => new Map((checklist.data?.itens ?? []).map((item) => [item.chave, item.rotulo])),
    [checklist.data],
  )

  return (
    <Dialog
      open={vistoria !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        {vistoria === null ? null : (
          <>
            <DialogHeader>
              <DialogTitle>{ROTULO_DO_TIPO_DE_VISTORIA[vistoria.tipo]}</DialogTitle>
              <DialogDescription>
                Registrada em {formatarDataHora(vistoria.criadoEm)} por{' '}
                <span title={vistoria.realizadaPorId ?? undefined}>
                  {resumoDoAutor(vistoria.realizadaPorId)}
                </span>
                .
              </DialogDescription>
            </DialogHeader>

            {vistoria.anulada ? (
              <Alert variant="danger">
                <AlertTitle>Vistoria anulada</AlertTitle>
                <AlertDescription>
                  {vistoria.anuladaEm === null
                    ? ''
                    : `Em ${formatarDataHora(vistoria.anuladaEm)}. `}
                  {vistoria.motivoDaAnulacao === null
                    ? 'Sem motivo registrado.'
                    : `Motivo: ${vistoria.motivoDaAnulacao}`}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-secondary">
              <span className="tabular-money">
                Hodômetro:{' '}
                <span className="font-medium text-foreground">
                  {formatarKm(vistoria.hodometro)}
                </span>
              </span>
              <span className="tabular-money">
                Combustível:{' '}
                <span className="font-medium text-foreground">
                  {rotuloDoNivelDeCombustivel(vistoria.combustivelEmOitavos)}
                </span>
              </span>
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Checklist</h3>
              {vistoria.respostas.length === 0 ? (
                <p className="text-sm text-fg-muted">
                  Nenhum item do checklist foi respondido nesta vistoria.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {vistoria.respostas.map((resposta) => {
                    const observacao = resposta.observacao ?? ''

                    return (
                      <li
                        key={resposta.chave}
                        className="flex flex-wrap items-center gap-2 rounded-lg border
                          border-border bg-surface px-3 py-2"
                      >
                        <span className="min-w-0 flex-1 text-sm text-foreground">
                          {rotuloDoItem.get(resposta.chave) ?? resposta.chave}
                        </span>
                        {resposta.ok ? (
                          <Badge variant="success">
                            <CircleCheck aria-hidden="true" />
                            OK
                          </Badge>
                        ) : (
                          <Badge variant="danger">
                            <TriangleAlert aria-hidden="true" />
                            Não OK
                          </Badge>
                        )}
                        {observacao.length === 0 ? null : (
                          <span className="w-full text-xs text-fg-secondary">
                            Obs.: {observacao}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Fotos ({String(vistoria.fotos.length)})
              </h3>
              {vistoria.fotos.length === 0 ? (
                <p className="text-sm text-fg-muted">Nenhuma foto nesta vistoria.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {vistoria.fotos.map((foto) => (
                    <FotoComRotulo key={foto.id} chave={foto.arquivoChave} angulo={foto.angulo} />
                  ))}
                </div>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  Avarias ({String(vistoria.avarias.length)})
                </h3>
                {vistoria.avarias.length > 0 ? <LegendaDeGravidade /> : null}
              </div>
              {vistoria.avarias.length === 0 ? (
                <p className="text-sm text-fg-muted">Nenhuma avaria apontada.</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <DiagramaDoCarro
                    avarias={vistoria.avarias}
                    somenteLeitura
                    rotulo={`Avarias da vistoria: ${ROTULO_DO_TIPO_DE_VISTORIA[vistoria.tipo]}`}
                  />
                  <ol className="min-w-0 space-y-2">
                    {vistoria.avarias.map((avaria, indice) => (
                      <ItemDeAvaria key={avaria.id} avaria={avaria} indice={indice} />
                    ))}
                  </ol>
                </div>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Assinatura</h3>
              <AssinaturaDoCliente chave={vistoria.assinaturaArquivoChave} />
            </section>

            <section className="space-y-1">
              <h3 className="text-sm font-semibold text-foreground">Observações</h3>
              <p className="whitespace-pre-wrap text-sm text-fg-secondary">
                {vistoria.observacoes ?? SEM_VALOR}
              </p>
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Comparativo
// ---------------------------------------------------------------------------

interface ParesDeFotosProps {
  readonly saida: Vistoria
  readonly retorno: Vistoria
}

/**
 * O par de fotos de cada ângulo, saída à esquerda e retorno à direita — é
 * assim que a avaria nova salta aos olhos antes de qualquer lista. Ângulo sem
 * foto em nenhum dos lados não vira linha vazia.
 */
function ParesDeFotos({ saida, retorno }: ParesDeFotosProps): ReactElement {
  const angulosPresentes = ANGULOS_DE_FOTO.filter(
    (angulo) =>
      saida.fotos.some((foto) => foto.angulo === angulo) ||
      retorno.fotos.some((foto) => foto.angulo === angulo),
  )

  if (angulosPresentes.length === 0) {
    return <p className="text-sm text-fg-muted">Nenhuma foto nas duas vistorias.</p>
  }

  return (
    <ul className="space-y-3">
      {angulosPresentes.map((angulo) => (
        <li key={angulo} className="rounded-lg border border-border bg-surface p-3">
          <p className="text-sm font-medium text-foreground">{ROTULO_DO_ANGULO[angulo]}</p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <LadoDoPar
              rotulo="Saída"
              angulo={angulo}
              fotos={saida.fotos.filter((foto) => foto.angulo === angulo)}
            />
            <LadoDoPar
              rotulo="Retorno"
              angulo={angulo}
              fotos={retorno.fotos.filter((foto) => foto.angulo === angulo)}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

interface LadoDoParProps {
  readonly rotulo: string
  readonly angulo: AnguloDeFoto
  readonly fotos: readonly Vistoria['fotos'][number][]
}

function LadoDoPar({ rotulo, angulo, fotos }: LadoDoParProps): ReactElement {
  return (
    <div className="min-w-0 space-y-1.5">
      <p className="text-xs font-medium text-fg-secondary">{rotulo}</p>
      {fotos.length === 0 ? (
        <div
          className="flex h-28 items-center justify-center rounded-md border border-dashed
            border-border text-xs text-fg-muted"
        >
          Sem foto deste ângulo
        </div>
      ) : (
        <div className={cn('grid gap-2', fotos.length > 1 && 'grid-cols-2')}>
          {fotos.map((foto) => (
            <FotoAssinada
              key={foto.id}
              chave={foto.arquivoChave}
              alt={`${rotulo} — ${ROTULO_DO_ANGULO[angulo]}`}
              className="h-28"
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface PainelDeVistoriaProps {
  readonly titulo: string
  readonly vistoria: Vistoria
}

function PainelDeVistoria({ titulo, vistoria }: PainelDeVistoriaProps): ReactElement {
  return (
    <div className="min-w-0 space-y-2 rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{titulo}</h3>
        <p className="text-xs tabular-money text-fg-secondary">
          {formatarKm(vistoria.hodometro)} ·{' '}
          {rotuloDoNivelDeCombustivel(vistoria.combustivelEmOitavos)} ·{' '}
          {formatarDataHora(vistoria.criadoEm)}
        </p>
      </div>

      <DiagramaDoCarro
        avarias={vistoria.avarias}
        somenteLeitura
        rotulo={`Avarias apontadas na vistoria: ${titulo}`}
      />

      {vistoria.avarias.length === 0 ? (
        <p className="text-xs text-fg-muted">Nenhuma avaria apontada.</p>
      ) : (
        <ol className="space-y-2">
          {vistoria.avarias.map((avaria, indice) => (
            <ItemDeAvaria key={avaria.id} avaria={avaria} indice={indice} />
          ))}
        </ol>
      )}
    </div>
  )
}

interface SecaoDoComparativoProps {
  readonly reservaId: string
}

/**
 * Saída × retorno com as avarias NOVAS — que vêm CALCULADAS pela API (a mesma
 * função determinística do shared que o fechamento usa). Recalcular aqui com
 * `avariasNovas` local abriria a porta para o painel mostrar três avarias e a
 * fatura cobrar quatro.
 */
function SecaoDoComparativo({ reservaId }: SecaoDoComparativoProps): ReactElement {
  const consulta = useComparativo(reservaId)

  if (consulta.isPending) {
    return <LoadingState linhas={2} rotulo="Carregando o comparativo…" />
  }

  if (consulta.isError) {
    return (
      <ErrorState
        titulo="Não foi possível carregar o comparativo"
        erro={consulta.error}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
      />
    )
  }

  const { saida, retorno, avariasNovas } = consulta.data

  if (saida === null || retorno === null) {
    const faltando = [
      saida === null ? 'a vistoria de saída' : null,
      retorno === null ? 'a vistoria de retorno' : null,
    ]
      .filter((parte): parte is string => parte !== null)
      .join(' e ')

    return (
      <EmptyState
        icone={ArrowRightLeft}
        titulo="Comparativo ainda não disponível"
        descricao={`Ainda falta ${faltando} (válida, não anulada). Sem as duas não há base de comparação — e é sobre as avarias novas que o fechamento pode cobrar.`}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">Fotos por ângulo, lado a lado</h3>
        <ParesDeFotos saida={saida} retorno={retorno} />
      </div>

      {/* Empilham abaixo de `md`: em 375px os dois diagramas lado a lado
          ficariam pequenos demais para os marcadores serem clicáveis ao olho. */}
      <div className="grid gap-4 md:grid-cols-2">
        <PainelDeVistoria titulo="Saída" vistoria={saida} />
        <PainelDeVistoria titulo="Retorno" vistoria={retorno} />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            Avarias novas ({String(avariasNovas.length)})
          </h3>
          <LegendaDeGravidade />
        </div>

        {avariasNovas.length === 0 ? (
          <Alert variant="success">
            <AlertTitle>Nenhuma avaria nova</AlertTitle>
            <AlertDescription>
              Tudo que o retorno apontou já existia na saída — não há avaria a cobrar no fechamento.
            </AlertDescription>
          </Alert>
        ) : (
          <ul className="space-y-2">
            {avariasNovas.map((avaria) => (
              <ItemDeAvaria key={avaria.id} avaria={avaria} nova />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export function VistoriaPage(): ReactElement {
  const podeRegistrar = usePermissao('inspection.create')
  const podeAnular = usePermissao('inspection.update')

  // `?? null` porque a rota pode não ter o parâmetro (endereço digitado à
  // mão): aí a tela é "não encontrada", não uma consulta com id vazio — o
  // mesmo padrão documentado em `veiculo-page.tsx`.
  const reservaId = useParams({ strict: false }).reservaId ?? null

  const vistorias = useVistoriasDaReserva(reservaId)
  const anular = useAnularVistoria(reservaId)

  const [vistoriaEmDetalhe, setVistoriaEmDetalhe] = useState<Vistoria | null>(null)
  const [vistoriaParaAnular, setVistoriaParaAnular] = useState<Vistoria | null>(null)
  const [motivoDaAnulacao, setMotivoDaAnulacao] = useState('')
  const [erroDaAnulacao, setErroDaAnulacao] = useState<string | null>(null)

  function abrirAnulacao(vistoria: Vistoria): void {
    setVistoriaParaAnular(vistoria)
    setMotivoDaAnulacao('')
    setErroDaAnulacao(null)
  }

  function fecharAnulacao(): void {
    setVistoriaParaAnular(null)
  }

  function confirmarAnulacao(): void {
    if (vistoriaParaAnular === null) {
      return
    }

    const motivo = motivoDaAnulacao.trim()

    // Mesmo mínimo do `anularVistoriaSchema` da API — validar antes poupa uma
    // ida ao servidor, mas a mensagem final é sempre a dele.
    if (motivo.length < 5) {
      setErroDaAnulacao('descreva o motivo da anulação — ele vai para a trilha da disputa')
      return
    }

    setErroDaAnulacao(null)
    anular.mutate(
      { vistoriaId: vistoriaParaAnular.id, motivo },
      {
        onSuccess: () => {
          toast.success('Vistoria anulada', {
            description: 'Ela continua na lista, marcada e com o motivo — a história não se apaga.',
          })
          fecharAnulacao()
        },
        onError: (causa: unknown) => {
          // Reserva finalizada responde 409 — é a API quem decide, não um
          // status cacheado no painel.
          toast.error('Não foi possível anular', { description: errorMessage(causa) })
        },
      },
    )
  }

  const colunas = useMemo<ColumnDef<Vistoria>[]>(
    () => [
      {
        id: 'tipo',
        header: 'Vistoria',
        accessorFn: (linha: Vistoria) => linha.tipo,
        cell: ({ row }) => {
          const vistoria: Vistoria = row.original

          return (
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="font-medium text-foreground">
                  {ROTULO_DO_TIPO_DE_VISTORIA[vistoria.tipo]}
                </p>
                {vistoria.anulada ? (
                  <Badge variant="danger">
                    <Ban aria-hidden="true" />
                    Anulada
                  </Badge>
                ) : null}
              </div>
              {vistoria.anulada && vistoria.motivoDaAnulacao !== null ? (
                <p className="max-w-prose text-xs text-fg-secondary">
                  Motivo: {vistoria.motivoDaAnulacao}
                </p>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'hodometro',
        header: 'Hodômetro',
        accessorFn: (linha: Vistoria) => linha.hodometro,
        cell: ({ row }) => (
          <span className="tabular-money text-sm text-foreground">
            {formatarKm(row.original.hodometro)}
          </span>
        ),
      },
      {
        id: 'combustivel',
        header: 'Combustível',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm tabular-money text-foreground">
            {rotuloDoNivelDeCombustivel(row.original.combustivelEmOitavos)}
          </span>
        ),
      },
      {
        id: 'registro',
        header: 'Registro',
        enableSorting: false,
        cell: ({ row }) => <MiniaturasDaLinha vistoria={row.original} />,
      },
      {
        id: 'quando',
        header: 'Registrada em',
        accessorFn: (linha: Vistoria) => linha.criadoEm,
        cell: ({ row }) => {
          const vistoria: Vistoria = row.original

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {formatarDataHora(vistoria.criadoEm)}
              </p>
              <p
                className="truncate text-xs tabular-money text-fg-secondary"
                title={vistoria.realizadaPorId ?? undefined}
              >
                por {resumoDoAutor(vistoria.realizadaPorId)}
              </p>
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const vistoria: Vistoria = row.original

          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setVistoriaEmDetalhe(vistoria)
                }}
              >
                <Eye aria-hidden="true" />
                Detalhes
              </Button>
              {/* Anulada não se anula de novo; o resto quem barra é a API (409
                  na reserva finalizada) — o botão só abre o diálogo. */}
              {podeAnular && !vistoria.anulada ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    abrirAnulacao(vistoria)
                  }}
                >
                  <Ban aria-hidden="true" />
                  Anular
                </Button>
              ) : null}
            </div>
          )
        },
      },
    ],
    // O que muda o conjunto de colunas é a permissão de anular.
    [podeAnular],
  )

  if (reservaId === null) {
    return <NotFoundState />
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Link
          to="/reservas/$reservaId"
          params={{ reservaId }}
          className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-secondary
            hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Detalhe da reserva
        </Link>

        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Vistorias da reserva</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            Saída e retorno com fotos por ângulo, avarias posicionadas e assinatura — é esta
            comparação que sustenta qualquer desconto de caução.
          </p>
        </div>
      </div>

      {podeRegistrar ? <FormularioDeRegistro reservaId={reservaId} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Vistorias registradas</CardTitle>
          <CardDescription>
            A trilha completa, na ordem em que aconteceram — as anuladas ficam marcadas com o
            motivo, nunca somem. “Detalhes” abre o checklist respondido, as fotos e a assinatura.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TabelaDeDados
            colunas={colunas}
            dados={vistorias.data?.itens ?? []}
            carregando={vistorias.isPending}
            erro={vistorias.error}
            aoTentarNovamente={() => {
              void vistorias.refetch()
            }}
            estadoVazio={
              <EmptyState
                icone={ClipboardList}
                titulo="Nenhuma vistoria registrada"
                descricao="A vistoria de saída é a que libera o carro — registre-a acima quando a retirada acontecer."
              />
            }
            legenda="Vistorias registradas nesta reserva"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Comparativo saída × retorno</CardTitle>
          <CardDescription>
            O par de fotos de cada ângulo lado a lado, e embaixo as avarias novas — apontadas no
            retorno e sem correspondente na saída, as únicas sobre as quais o fechamento pode
            cobrar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SecaoDoComparativo reservaId={reservaId} />
        </CardContent>
      </Card>

      <DialogoDeDetalhe
        vistoria={vistoriaEmDetalhe}
        aoFechar={() => {
          setVistoriaEmDetalhe(null)
        }}
      />

      <Dialog
        open={vistoriaParaAnular !== null}
        onOpenChange={(proximo) => {
          if (!proximo) {
            fecharAnulacao()
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Anular vistoria</DialogTitle>
            <DialogDescription>
              {vistoriaParaAnular === null
                ? ''
                : `${ROTULO_DO_TIPO_DE_VISTORIA[vistoriaParaAnular.tipo]} de ${formatarDataHora(vistoriaParaAnular.criadoEm)}. `}
              Ela continua na lista, marcada e com o motivo — anular tira do jogo, não apaga.
            </DialogDescription>
          </DialogHeader>

          <Campo
            rotulo="Motivo da anulação"
            obrigatorio
            erro={erroDaAnulacao ?? undefined}
            dica="Mínimo de 5 caracteres. É ele que responde “por que houve duas vistorias” numa disputa."
          >
            {(campo) => (
              <Textarea
                {...campo}
                value={motivoDaAnulacao}
                onChange={(evento) => {
                  setMotivoDaAnulacao(evento.target.value)
                }}
                disabled={anular.isPending}
              />
            )}
          </Campo>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={fecharAnulacao}
              disabled={anular.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmarAnulacao}
              disabled={anular.isPending}
            >
              {anular.isPending ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <Ban aria-hidden="true" />
              )}
              Anular vistoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
